import { track } from './amplitude';
import {
  SCENE_POI_VISITED,
  SCENE_COMPLETED,
  NEXT_SCENE_HINTED,
} from './events';
import { tourReceived } from '../state/visitedSights';
import type { TourItem } from '../shared/assistantMessage';
import { sceneEntered } from './sceneTracker';

/**
 * Backend-authoritative progress events derived from `tour: TourItem[]`
 * payload on every assistant_message.
 *
 * Diff'ing strategy: we keep a per-scene `prevState` snapshot in memory
 * and compare incoming `tour` against it. Newly-visited sights fire
 * `Scene POI Visited`; first-time fully-visited scene fires
 * `Scene Completed`; first-time `{kind: 'scene'}` item fires
 * `Next Scene Hinted`. All "first-time" milestones are session-scoped
 * (per scene id) — re-entry of an already-completed scene does NOT
 * re-fire `Scene Completed`.
 *
 * `POI Viewed` (UI-driven click intent) is tracked separately in
 * `tourTracker.ts` and complements these events.
 */

type SceneProgressState = {
  visited: Set<string>;
  completedEmitted: boolean;
  nextSceneEmitted: boolean;
};

const progressByScene = new Map<string, SceneProgressState>();
const sceneEnteredAt = new Map<string, number>();

const isSightItem = (it: TourItem): it is Extract<TourItem, { kind: 'sight' }> =>
  it.kind === 'sight';

const isSceneItem = (it: TourItem): it is Extract<TourItem, { kind: 'scene' }> =>
  it.kind === 'scene';

export const startProgressTracking = () => {
  sceneEntered.watch((sceneId) => {
    sceneEnteredAt.set(sceneId, performance.now());
  });

  tourReceived.watch(({ sceneId, items }) => {
    const sights = items.filter(isSightItem);
    const sceneItem = items.find(isSceneItem) ?? null;

    const currentVisited = new Set<string>(
      sights.filter((s) => s.visited).map((s) => String(s.sight_id)),
    );

    const prev =
      progressByScene.get(sceneId) ??
      ({
        visited: new Set<string>(),
        completedEmitted: false,
        nextSceneEmitted: false,
      } satisfies SceneProgressState);

    const totalPois = sights.length;

    // 1. Diff: newly-visited POIs since last tour payload.
    for (const sightId of currentVisited) {
      if (prev.visited.has(sightId)) continue;
      track(SCENE_POI_VISITED, {
        sight_id: sightId,
        scene_id: sceneId,
        visited_count: currentVisited.size,
        total_pois: totalPois,
        is_scene_first_visit: prev.visited.size === 0,
      });
    }

    // 2. Milestone: all POIs visited (first time in this session for this scene).
    const allVisited =
      totalPois > 0 && currentVisited.size === totalPois;
    const completedJustNow = allVisited && !prev.completedEmitted;
    if (completedJustNow) {
      const enteredAt = sceneEnteredAt.get(sceneId);
      const durationMs =
        enteredAt != null ? Math.round(performance.now() - enteredAt) : null;
      track(SCENE_COMPLETED, {
        scene_id: sceneId,
        total_pois: totalPois,
        duration_ms: durationMs,
      });
    }

    // 3. Next-scene hint (first time in this session for this scene).
    if (sceneItem !== null && !prev.nextSceneEmitted) {
      track(NEXT_SCENE_HINTED, {
        target_scene_id: sceneItem.scene_id,
        from_scene_id: sceneId,
        // Если хинт пришёл одновременно с achievement'ом «все POI visited»,
        // это нормальный кейс (бек именно так и шлёт). Отметка позволяет
        // отличить редкий случай хинта без полного прохождения.
        all_pois_visited: allVisited,
      });
    }

    progressByScene.set(sceneId, {
      visited: currentVisited,
      completedEmitted: prev.completedEmitted || completedJustNow,
      nextSceneEmitted: prev.nextSceneEmitted || sceneItem !== null,
    });
  });
};
